import { computed, ref, watch } from 'vue'
import {
  startOfDay,
  startOfHour,
  startOfWeek,
  startOfMonth,
  startOfQuarter,
  startOfYear,
  startOfMinute,
  addMinutes,
  addHours,
  addDays,
  addWeeks,
  addMonths,
  addQuarters,
  addYears
} from 'date-fns'
import { defineStore } from 'pinia'
import { useStore } from 'vuex'
import { LocaleString } from '@/components/types'

const DEFAULT_DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric'
}

export type DateTimeEdgeType =
  | 'now'
  | 'minute'
  | 'hour'
  | 'day'
  | 'week'
  | 'month'
  | 'quarter'
  | 'year'
export type DateTimeEdge = DateTimeEdgeType | { absolute: string }

export type DateTimePoint =
  | [DateTimeEdge, number?]
  | [DateTimeEdge, DateTimeEdge, number?]
  | DateTimeEdge
export type DateTimeRange = [DateTimePoint, DateTimePoint]

export type AbsoluteRange = [string, string]

export function edgeToDate(edge: DateTimeEdge, now: Date): Date {
  switch (edge) {
    case 'now':
      return now
    case 'minute':
      return startOfMinute(now)
    case 'hour':
      return startOfHour(now)
    case 'day':
      return startOfDay(now)
    case 'week':
      return startOfWeek(now)
    case 'month':
      return startOfMonth(now)
    case 'quarter':
      return startOfQuarter(now)
    case 'year':
      return startOfYear(now)
    default:
      return new Date(edge.absolute)
  }
}

export function offsetDate(date: Date, edge: DateTimeEdge, amount?: number): Date {
  if (amount === undefined) {
    return date
  }
  switch (edge) {
    case 'minute':
      return addMinutes(date, amount)
    case 'hour':
      return addHours(date, amount)
    case 'day':
      return addDays(date, amount)
    case 'week':
      return addWeeks(date, amount)
    case 'month':
      return addMonths(date, amount)
    case 'quarter':
      return addQuarters(date, amount)
    case 'year':
      return addYears(date, amount)
    default:
      return date
  }
}

export function resolvePoint(point: DateTimePoint, now: Date): Date {
  if (typeof point === 'string') {
    return edgeToDate(point, now)
  } else if (Array.isArray(point)) {
    if (point.length === 2) {
      const [edge, amount] = point as [DateTimeEdge, number]
      const date = offsetDate(edgeToDate(edge, now), edge, amount)
      return date
    } else {
      const [trunc, res, amount] = point as [DateTimeEdge, DateTimeEdge, number]
      const date = edgeToDate(trunc, now)
      const resolved = offsetDate(date, res, amount)
      return resolved
    }
  } else {
    return edgeToDate(point, now)
  }
}

export function dateToISOString(d: Date): string {
  const date = d.toLocaleDateString('sv')
  const time = d.toLocaleTimeString('sv')
  return `${date}T${time}`
}

export function resolveRange(range: DateTimeRange, now?: Date): AbsoluteRange {
  const date = now ?? new Date()
  const [startPoint, endPoint] = range
  const start = dateToISOString(resolvePoint(startPoint, date))
  const end = dateToISOString(resolvePoint(endPoint, date))
  return start < end ? [start, end] : [end, start]
}

export function formatRange(
  range: DateTimeRange,
  formatOptions: Intl.DateTimeFormatOptions = DEFAULT_DATE_FORMAT_OPTIONS,
  now?: Date
): LocaleString {
  const absoluteRange = resolveRange(range, now)
  const from = new Date(absoluteRange[0])
  const to = new Date(absoluteRange[1])
  return {
    sv: new Intl.DateTimeFormat('sv', formatOptions).formatRange(from, to),
    en: new Intl.DateTimeFormat('en', formatOptions).formatRange(from, to)
  }
}

function rangeIsLive(range: DateTimeRange): boolean {
  return range[0] === 'now' || range[1] === 'now'
}

export const useTimeSelection = defineStore('timeSelection', () => {
  const interval = ref<number>(60 * 1000 * 10)
  const intervalId = ref<NodeJS.Timeout>()
  const range = ref<DateTimeRange>()
  const selectionName = ref<string>()
  const absoluteRange = ref<AbsoluteRange>()
  const store = useStore()
  const access = computed<string[]>(() => store.state.featureAccess)
  const hasLiveAccess = computed(() => access.value.includes('FEAT_ANALYSIS-LIVE_READER'))
  const shouldUpdate = computed(
    () => range.value && hasLiveAccess.value && rangeIsLive(range.value)
  )
  const timeRangeFormatOptions = ref<Intl.DateTimeFormatOptions>(DEFAULT_DATE_FORMAT_OPTIONS)

  const timeRangeFormat = computed<LocaleString | undefined>(() => {
    if (!range.value) return undefined
    return formatRange(range.value, timeRangeFormatOptions.value)
  })

  watch(
    shouldUpdate,
    (shouldUpdate) => {
      if (shouldUpdate) {
        startInterval()
      } else {
        stopInterval()
      }
    },
    { immediate: true }
  )

  function startInterval() {
    if (!interval.value) return
    if (intervalId.value) return
    intervalId.value = setInterval(update, interval.value)
  }

  function stopInterval() {
    if (intervalId.value) {
      clearInterval(intervalId.value)
      intervalId.value = undefined
    }
  }

  function set(name: string, rangeValue: DateTimeRange) {
    range.value = rangeValue
    selectionName.value = name
    update()
  }

  function update() {
    if (range.value) {
      absoluteRange.value = resolveRange(range.value)
    }
  }

  return {
    range,
    absoluteRange,
    selectionName,
    timeRangeFormat,
    update,
    set
  }
})
